import { useEffect, useRef, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import {
  Tile,
  Tag,
  Button,
  TextInput,
  PasswordInput,
  Stack,
  InlineNotification,
  Loading,
} from '@carbon/react';
import { User, TrashCan } from '@carbon/icons-react';
import { getClerkErrorMessage } from '@/lib/clerkErrors';

function getInitials(firstName, lastName, email) {
  const first = firstName?.charAt(0) || '';
  const last = lastName?.charAt(0) || '';
  if (first || last) return `${first}${last}`.toUpperCase();
  return email?.charAt(0)?.toUpperCase() || '?';
}

const SettingsPage = () => {
  const { isLoaded, user } = useUser();
  const fileInputRef = useRef(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setEmail(user.primaryEmailAddress?.emailAddress || '');
  }, [user]);

  const showNotice = (kind, title, subtitle) => {
    setNotice({ kind, title, subtitle });
    setError('');
  };

  const handleProfileSave = async (event) => {
    event.preventDefault();
    if (!user) return;

    setProfileSaving(true);
    setError('');
    setNotice(null);

    try {
      await user.update({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      showNotice('success', 'Profile updated', 'Your name has been saved.');
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setProfileSaving(false);
    }
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    if (!user || !file) return;

    setAvatarSaving(true);
    setError('');
    setNotice(null);

    try {
      await user.setProfileImage({ file });
      showNotice('success', 'Avatar updated', 'Your profile photo has been saved.');
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setAvatarSaving(false);
      event.target.value = '';
    }
  };

  const handleAvatarRemove = async () => {
    if (!user) return;

    setAvatarSaving(true);
    setError('');
    setNotice(null);

    try {
      await user.setProfileImage({ file: null });
      showNotice('success', 'Avatar removed', 'Your profile photo has been removed.');
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setAvatarSaving(false);
    }
  };

  const handlePasswordSave = async (event) => {
    event.preventDefault();
    if (!user) return;

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    setPasswordSaving(true);
    setError('');
    setNotice(null);

    try {
      await user.updatePassword({
        currentPassword,
        newPassword,
        signOutOfOtherSessions: true,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showNotice('success', 'Password updated', 'Your password has been changed.');
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setPasswordSaving(false);
    }
  };

  if (!isLoaded) {
    return (
      <div className="app-page settings-page" data-testid="settings-page">
        <Loading withOverlay={false} description="Loading settings..." />
      </div>
    );
  }

  return (
    <div className="app-page settings-page" data-testid="settings-page">
      <div className="section-header settings-header">
        <div>
          <Tag type="blue">SETTINGS</Tag>
          <h2 className="section-title">Account settings</h2>
          <p className="section-description">
            Manage your profile, avatar, and password.
          </p>
        </div>
      </div>

      {notice && (
        <InlineNotification
          kind={notice.kind}
          title={notice.title}
          subtitle={notice.subtitle}
          onClose={() => setNotice(null)}
          className="settings-notice"
        />
      )}

      {error && (
        <InlineNotification
          kind="error"
          title="Something went wrong"
          subtitle={error}
          onClose={() => setError('')}
          className="settings-notice"
        />
      )}

      <div className="settings-grid">
        <Tile className="settings-card">
          <h3 className="settings-card__title">Profile</h3>
          <div className="settings-avatar-row">
            <div className="settings-avatar">
              {user?.imageUrl ? (
                <img src={user.imageUrl} alt="" className="settings-avatar__image" />
              ) : (
                <div className="settings-avatar__placeholder" aria-hidden="true">
                  {getInitials(user?.firstName, user?.lastName, email)}
                </div>
              )}
            </div>
            <div className="settings-avatar-actions">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="settings-avatar-input"
                onChange={handleAvatarChange}
              />
              <Button
                kind="secondary"
                size="sm"
                renderIcon={User}
                disabled={avatarSaving}
                onClick={() => fileInputRef.current?.click()}
              >
                {avatarSaving ? 'Saving…' : 'Upload photo'}
              </Button>
              {user?.imageUrl && (
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={TrashCan}
                  disabled={avatarSaving}
                  onClick={handleAvatarRemove}
                >
                  Remove
                </Button>
              )}
            </div>
          </div>

          <form className="settings-form" onSubmit={handleProfileSave}>
            <Stack gap={6}>
              <TextInput
                id="settings-first-name"
                labelText="First name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                autoComplete="given-name"
              />
              <TextInput
                id="settings-last-name"
                labelText="Last name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                autoComplete="family-name"
              />
              <TextInput
                id="settings-email"
                labelText="Email"
                value={email}
                readOnly
                helperText="Contact support to change your email address."
              />
              <Button type="submit" disabled={profileSaving}>
                {profileSaving ? 'Saving…' : 'Save profile'}
              </Button>
            </Stack>
          </form>
        </Tile>

        <Tile className="settings-card">
          <h3 className="settings-card__title">Password</h3>
          <form className="settings-form" onSubmit={handlePasswordSave}>
            <Stack gap={6}>
              <PasswordInput
                id="settings-current-password"
                labelText="Current password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
              <PasswordInput
                id="settings-new-password"
                labelText="New password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
              <PasswordInput
                id="settings-confirm-password"
                labelText="Confirm new password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
              <Button type="submit" disabled={passwordSaving}>
                {passwordSaving ? 'Updating…' : 'Update password'}
              </Button>
            </Stack>
          </form>
        </Tile>
      </div>
    </div>
  );
};

export default SettingsPage;
